import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, downloadUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Hammer, CheckCircle2, Tag, Download, AlertTriangle, ArrowLeft, Plus, X, Lock, Unlock, Cog, Hash, Scale, Package, CalendarClock, Printer, Repeat, FileText, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { unitLabel, imballoOf } from "@/lib/imballo";
import { SHIFTS, SHIFT_LABEL, shiftAt } from "@/lib/turni";

const TURNO_ORDER = { I: 1, II: 2, III: 3 };

function calcPesi(art) {
    if (!art) return {};
    const l = art.lunghezza || 0;
    const w = (art.larghezza_principale || 0) + (art.larghezza_soffietto || 0);
    const s = art.spessore_my || 0;
    if (!l || !w || !s) return { wTot: w };
    const busta = l * w * s * 0.000092 * 2;
    const rotolo = busta * (art.buste_per_rotolo || 0);
    const cartone = rotolo * (art.rotoli_per_cartone || 0) / 1000;
    return {
        wTot: w,
        busta: busta > 0 ? busta.toFixed(2) : null,
        rotolo: rotolo > 0 ? Math.round(rotolo).toString() : null,
        cartone: cartone > 0 ? cartone.toFixed(1) : null,
    };
}

// Peso su ordine effettivo (con override)
function calcPesiOrdine(o) {
    const l = o.lunghezza_eff || 0;
    const w = (o.larghezza_principale_eff || 0) + (o.larghezza_soffietto_eff || 0);
    const s = o.spessore_my || 0;
    if (!l || !w || !s) return { wTot: w };
    const busta = l * w * s * 0.000092 * 2;
    const rotolo = busta * (o.buste_per_rotolo_eff || 0);
    const cartone = rotolo * (o.rotoli_per_cartone_eff || 0) / 1000;
    return { wTot: w, busta: busta.toFixed(2), rotolo: Math.round(rotolo).toString(), cartone: cartone.toFixed(1) };
}

// Riga singolo lotto — bottoni Stampa / Ristampa / Download ZPL / Elimina
function LottoRow({ l, user, onDone, onDelete }) {
    const [busy, setBusy] = useState(false);
    const isOp = user?.role === "operatore" || user?.role === "admin";
    const alreadyPrinted = !!l.stampato_at;
    // Un lotto è cancellabile se non è ancora stato caricato su una pedana attiva.
    // Ufficio/admin possono cancellare qualsiasi lotto; l'operatore solo i propri.
    const canDelete = ((l.cartoni_impegnati || 0) === 0) && !l.pedana_id && (
        user?.role === "admin" || user?.role === "ufficio" ||
        (user?.role === "operatore" && l.operatore_id === user?.id)
    );
    const doStampa = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/lotti/${l.id}/stampa`);
            if (data.sent) toast.success(`Stampa inviata a ${data.printer} (${data.cartoni} copie)`);
            else toast.warning(data.message || "Stampante non configurata — scarico ZPL");
            if (!data.sent && data.zpl) window.open(downloadUrl(`/lotti/${l.id}/label.zpl`), "_blank");
            onDone && onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const doRistampa = async () => {
        if (!window.confirm(`Ristampa etichetta lotto ${l.numero}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/lotti/${l.id}/ristampa`);
            if (data.sent) toast.success(`Ristampa inviata (${data.cartoni} copie)`);
            else { toast.warning(data.message || "Stampante non configurata — scarico ZPL"); window.open(downloadUrl(`/lotti/${l.id}/label.zpl`), "_blank"); }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <tr className="border-b border-slate-800/60" data-testid={`row-lotto-${l.numero}`}>
            <td className="px-5 py-3 doc-code text-xs">
                {l.numero}
                {l.alternativa && <Badge className="ml-2 badge-pill bg-violet-500/15 text-violet-300 border-violet-500/40">EXTRA</Badge>}
            </td>
            <td className="px-5 py-3 doc-code text-xs">{l.ordine_numero}</td>
            <td className="px-5 py-3"><span className="font-mono text-amber-300 text-xs">{l.codice}</span> · {l.descrizione}</td>
            <td className="px-5 py-3 text-right font-mono font-bold">{l.cartoni}</td>
            <td className="px-5 py-3 text-slate-400 text-xs">{l.operatore_nome}</td>
            <td className="px-5 py-3 text-xs">
                {alreadyPrinted
                    ? <span className="text-emerald-400" title={l.stampato_at}>✓ stampato</span>
                    : <span className="text-slate-500">— non stampato</span>}
            </td>
            <td className="px-5 py-3 text-right whitespace-nowrap">
                {!alreadyPrinted && (
                    <button onClick={doStampa} disabled={busy} data-testid={`button-stampa-${l.numero}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold disabled:opacity-40 mr-1">
                        <Printer size={12} /> Stampa
                    </button>
                )}
                {isOp && (
                    <button onClick={doRistampa} disabled={busy} data-testid={`button-ristampa-${l.numero}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs disabled:opacity-40 mr-1"
                            title="Ristampa (non modifica flag)">
                        <Repeat size={12} /> Ristampa
                    </button>
                )}
                <a href={downloadUrl(`/lotti/${l.id}/label.zpl`)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800/50 hover:bg-slate-700 text-slate-300 text-xs"
                   title="Scarica ZPL">
                    <Download size={12} />
                </a>
                {canDelete && onDelete && (
                    <button onClick={()=>onDelete(l)} disabled={busy} data-testid={`button-del-lotto-${l.numero}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/25 text-red-300 text-xs disabled:opacity-40 ml-1"
                            title="Elimina lotto (correggi errore)">
                        <Trash2 size={12} />
                    </button>
                )}
            </td>
        </tr>
    );
}

export default function ProduzioneOperatore() {
    const { user } = useAuth();
    const [impianti, setImpianti] = useState([]);
    const [orders, setOrders] = useState([]);
    const [articles, setArticles] = useState([]);
    const [fascette, setFascette] = useState([]);
    const [tipiPed, setTipiPed] = useState([]);
    const [cartoniAnag, setCartoniAnag] = useState([]);
    const [colori, setColori] = useState([]);
    const [lotti, setLotti] = useState([]);
    const [loading, setLoading] = useState(true);

    // Selezioni operatore (persistite)
    const [impiantoId, setImpiantoId] = useState(() => localStorage.getItem(`op-impianto-${user?.id}`) || "");
    const [turno, setTurno] = useState(() => localStorage.getItem(`op-turno-${user?.id}-${new Date().toISOString().slice(0,10)}`) || "");
    const [tab, setTab] = useState(() => (user?.role === "ufficio" || user?.role === "admin") ? "tutti" : "miei");
    const [selectedOrder, setSelectedOrder] = useState(null); // vista dettaglio ordine
    const autoShiftDone = useRef(false);

    useEffect(() => { if (impiantoId) localStorage.setItem(`op-impianto-${user?.id}`, impiantoId); }, [impiantoId, user?.id]);
    useEffect(() => {
        if (turno) localStorage.setItem(`op-turno-${user?.id}-${new Date().toISOString().slice(0,10)}`, turno);
    }, [turno, user?.id]);

    const load = async () => {
        setLoading(true);
        try {
            const [imp, a, f, t, c, co, l] = await Promise.all([
                api.get("/impianti/"), api.get("/articles"), api.get("/fascette/"),
                api.get("/tipi-pedana/"), api.get("/cartoni/"), api.get("/colori/"), api.get("/lotti"),
            ]);
            setImpianti(imp.data); setArticles(a.data); setFascette(f.data);
            setTipiPed(t.data); setCartoniAnag(c.data); setColori(co.data); setLotti(l.data.slice(0, 15));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Fetch orders quando cambio impianto
    useEffect(() => {
        if (!impiantoId) { setOrders([]); return; }
        (async () => {
            const { data } = await api.get(`/ordini?impianto_id=${impiantoId}`);
            // Sort: rispetta l'ordine cronologico di ufficio produzione — data_lavorazione ASC,
            // turno ASC (I → II → III), sort_in_shift ASC, created_at ASC
            const TURNO = { I: 1, II: 2, III: 3 };
            const emptyLast = v => v || "9999-12-31";
            data.sort((a,b) => {
                const dc = emptyLast(a.data_lavorazione).localeCompare(emptyLast(b.data_lavorazione));
                if (dc !== 0) return dc;
                const tc = (TURNO[a.turno]||99) - (TURNO[b.turno]||99);
                if (tc !== 0) return tc;
                const sc = (a.sort_in_shift||9999) - (b.sort_in_shift||9999);
                if (sc !== 0) return sc;
                return (a.created_at || "").localeCompare(b.created_at || "");
            });
            setOrders(data);
        })();
    }, [impiantoId]);

    // Auto-apri ordine da QR (?id=...) — cerca in TUTTI gli ordini (anche senza impianto selezionato)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const qid = params.get("id");
        if (!qid || selectedOrder) return;
        (async () => {
            try {
                const { data } = await api.get("/ordini");
                const found = data.find(o => o.id === qid || o.numero === qid);
                if (found) {
                    if (found.impianto_id) setImpiantoId(found.impianto_id);
                    if (found.turno) setTurno(found.turno);
                    setSelectedOrder(found);
                } else {
                    toast.error(`Ordine ${qid} non trovato`);
                }
            } catch (e) { console.error("auto-open ordine failed", e); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Default turno: priorità → ordine "mio" di oggi già assegnato, poi ora corrente (fasce ufficiali).
    // Si esegue una sola volta per sessione e non sovrascrive scelte manuali/persistite.
    useEffect(() => {
        if (autoShiftDone.current || turno || loading || !user?.id) return;
        autoShiftDone.current = true;
        const today = new Date().toISOString().slice(0,10);
        const mine = orders.find(o => o.operatore_id === user.id && o.data_lavorazione === today && o.turno);
        if (mine) { setTurno(mine.turno); return; }
        setTurno(shiftAt());
    }, [orders, turno, user?.id, loading]);

    const today = new Date().toISOString().slice(0,10);

    const filteredOrders = useMemo(() => {
        if (!impiantoId) return [];
        if (tab === "chiusi") return orders.filter(o => o.stato === "chiuso");
        const open = orders.filter(o => o.stato === "aperto");
        if (tab === "miei") return open.filter(o =>
            // Ordini multi-operatore: sempre visibili a chiunque (finché aperti).
            // Ordini singolo operatore: solo se assegnati a me E data di oggi (o senza data).
            o.multi_operatore ||
            (o.operatore_id === user?.id && (!o.data_lavorazione || o.data_lavorazione === today))
        );
        if (tab === "tutti") return open;
        const curT = TURNO_ORDER[turno] || 0;
        if (tab === "prima") {
            return open.filter(o => (o.cartoni_prodotti || 0) < o.cartoni_pianificati && (
                (o.data_lavorazione && o.data_lavorazione < today) ||
                (o.data_lavorazione === today && curT > 0 && TURNO_ORDER[o.turno] > 0 && TURNO_ORDER[o.turno] < curT)
            ));
        }
        if (tab === "dopo") {
            return open.filter(o =>
                (o.data_lavorazione && o.data_lavorazione > today) ||
                (o.data_lavorazione === today && curT > 0 && TURNO_ORDER[o.turno] > curT)
            );
        }
        return open;
    }, [orders, tab, user?.id, impiantoId, turno, today]);

    const openMacchinaCount = useMemo(() => orders.filter(o => o.stato === "aperto").length, [orders]);
    const mieiCount = useMemo(() => orders.filter(o => o.stato === "aperto" && (
        o.multi_operatore ||
        (o.operatore_id === user?.id && (!o.data_lavorazione || o.data_lavorazione === today))
    )).length, [orders, user?.id, today]);
    const chiusiCount = useMemo(() => orders.filter(o => o.stato === "chiuso").length, [orders]);

    const cruscottoTurno = useMemo(() => {
        if (!impiantoId || !turno) return [];
        return orders.filter(o => o.stato === "aperto" && o.operatore_id === user?.id && o.data_lavorazione === today && o.turno === turno)
                     .sort((a,b) => (a.numero || "").localeCompare(b.numero || ""));
    }, [orders, impiantoId, turno, user?.id, today]);

    const refreshOrders = async () => {
        if (!impiantoId) return;
        try {
            const { data } = await api.get(`/ordini?impianto_id=${impiantoId}`);
            const TURNO = { I: 1, II: 2, III: 3 };
            const emptyLast = v => v || "9999-12-31";
            data.sort((a,b) => {
                const dc = emptyLast(a.data_lavorazione).localeCompare(emptyLast(b.data_lavorazione));
                if (dc !== 0) return dc;
                const tc = (TURNO[a.turno]||99) - (TURNO[b.turno]||99);
                if (tc !== 0) return tc;
                const sc = (a.sort_in_shift||9999) - (b.sort_in_shift||9999);
                if (sc !== 0) return sc;
                return (a.created_at || "").localeCompare(b.created_at || "");
            });
            setOrders(data);
            setSelectedOrder(prev => prev ? (data.find(o => o.id === prev.id) || null) : prev);
            const l = await api.get("/lotti"); setLotti(l.data.slice(0, 15));
        } catch (e) { console.error("refreshOrders failed", e); }
    };

    const delLotto = async (l) => {
        if (!window.confirm(`Eliminare lotto ${l.numero} (${l.cartoni} cartoni)?\nOperazione non annullabile — utile solo per correggere un inserimento errato.`)) return;
        try {
            await api.delete(`/lotti/${l.id}`);
            toast.success(`Lotto ${l.numero} eliminato`);
            await refreshOrders();
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (selectedOrder) {
        return <OrderDetailView order={selectedOrder} user={user} articles={articles} fascette={fascette}
                                tipiPed={tipiPed} cartoniAnag={cartoniAnag} colori={colori} impianti={impianti}
                                onRefresh={refreshOrders}
                                onBack={()=>{
                                    setSelectedOrder(null);
                                    // Rimuovi ?id=... dall'URL così l'auto-open non ri-scatta e il tasto menù torna a funzionare
                                    if (window.location.search) {
                                        window.history.replaceState({}, "", window.location.pathname);
                                    }
                                    load();
                                }} />;
    }

    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-emerald-400/80">POSTAZIONE OPERATORE</div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Produzione saldatrici</h1>
                <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                    Seleziona la macchina su cui stai lavorando per vedere gli ordini attivi.
                </p>
            </div>

            {/* Selettori Macchina + Turno */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs text-slate-400 flex items-center gap-2"><Cog size={12} /> Macchina *</Label>
                    <Select value={impiantoId} onValueChange={setImpiantoId}>
                        <SelectTrigger data-testid="select-op-impianto" className="bg-slate-950/50 border-slate-800 mt-1 h-12">
                            <SelectValue placeholder="Seleziona macchina…" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                            {impianti.map(i => <SelectItem key={i.id} value={i.id}>
                                <span className="font-mono text-amber-300 mr-2">{i.codice}</span>{i.descrizione}
                            </SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs text-slate-400 flex items-center gap-2"><CalendarClock size={12} /> Turno attuale</Label>
                    <Select value={turno} onValueChange={setTurno}>
                        <SelectTrigger data-testid="select-op-turno" className="bg-slate-950/50 border-slate-800 mt-1 h-12">
                            <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                            {SHIFTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {!impiantoId && (
                <Card className="card p-10 text-center text-slate-500">
                    <Hammer size={40} className="mx-auto mb-3 opacity-40" />
                    <div className="text-sm">Seleziona la macchina su cui stai lavorando per vedere gli ordini</div>
                </Card>
            )}

            {impiantoId && (
                <>
                    {/* Cruscotto Turno */}
                    <Card className="card p-4" data-testid="cruscotto-turno">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="text-[10px] uppercase tracking-widest text-amber-300">
                                Cruscotto turno {turno ? `${turno} · ${today}` : "(seleziona turno)"}
                            </div>
                        </div>
                        {!turno && <div className="text-sm text-slate-500 italic">Seleziona un turno per vedere il tuo cruscotto</div>}
                        {turno && cruscottoTurno.length === 0 && (
                            <div className="text-sm text-slate-500 italic">Nessun ordine assegnato per il tuo turno di oggi su questa macchina</div>
                        )}
                        {cruscottoTurno.map(o => (
                            <button key={o.id} onClick={()=>setSelectedOrder(o)} data-testid={`cruscotto-row-${o.numero}`}
                                    className="w-full flex justify-between items-center py-2 border-b border-slate-800/60 last:border-0 hover:bg-slate-800/30 rounded px-2 text-left">
                                <div>
                                    <div className="doc-code text-[11px]">{o.numero}</div>
                                    <div className="text-sm text-slate-200">{o.descrizione}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-lg text-emerald-300 font-bold">{o.cartoni_pianificati}</div>
                                    <div className="text-[10px] text-slate-500">cartoni</div>
                                </div>
                            </button>
                        ))}
                    </Card>

                    {/* Tabs */}
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList data-testid="tabs-vista-ordini" className="bg-slate-900 border border-slate-800 flex-wrap">
                            <TabsTrigger value="miei" data-testid="tab-miei">I miei ({mieiCount})</TabsTrigger>
                            <TabsTrigger value="prima" data-testid="tab-prima">Prima di me — non finiti</TabsTrigger>
                            <TabsTrigger value="dopo" data-testid="tab-dopo">Dopo di me</TabsTrigger>
                            <TabsTrigger value="tutti" data-testid="tab-tutti">Tutti sulla macchina ({openMacchinaCount})</TabsTrigger>
                            <TabsTrigger value="chiusi" data-testid="tab-chiusi">Chiusi ({chiusiCount})</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <div className="grid gap-4 lg:grid-cols-2" data-testid="orders-list">
                        {loading && <Card className="card p-8 text-center text-slate-500 text-xs">Caricamento…</Card>}
                        {!loading && filteredOrders.length === 0 && (
                            <Card className="card p-8 text-center text-slate-500 lg:col-span-2">
                                <Hammer size={40} className="mx-auto mb-3 opacity-40" />
                                <div className="text-sm">
                                    {tab === "miei" && mieiCount === 0 && openMacchinaCount > 0
                                        ? `Nessun ordine assegnato a te. Ci sono ${openMacchinaCount} ordini su questa macchina — vedi "Tutti sulla macchina".`
                                        : "Nessun ordine in questa vista"}
                                </div>
                                {tab === "miei" && mieiCount === 0 && openMacchinaCount > 0 && (
                                    <button onClick={()=>setTab("tutti")} data-testid="button-vai-tutti"
                                            className="mt-3 text-emerald-300 hover:text-emerald-200 text-xs underline">
                                        Vedi tutti gli ordini della macchina →
                                    </button>
                                )}
                            </Card>
                        )}
                        {filteredOrders.map(o => {
                            const perc = o.cartoni_pianificati > 0 ? Math.min(100, Math.round(100 * o.cartoni_prodotti / o.cartoni_pianificati)) : 0;
                            return (
                                <Card key={o.id} className="card p-5 cursor-pointer hover:border-emerald-500/40" onClick={()=>setSelectedOrder(o)} data-testid={`prod-card-${o.numero}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="doc-code text-sm">{o.numero}</div>
                                            <div className="font-display text-xl font-bold mt-1">{o.codice}</div>
                                            <div className="text-sm text-slate-400">{o.descrizione}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            {o.auto && <Badge className="badge-pill bg-violet-500/15 text-violet-300 border-violet-500/40">AUTO</Badge>}
                                            {o.multi_operatore && <Badge className="badge-pill bg-amber-500/15 text-amber-300 border-amber-500/40">MULTI-OP</Badge>}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center text-[10px] text-slate-500 mb-1 font-mono">
                                        <span>{o.data_lavorazione ? new Date(o.data_lavorazione).toLocaleDateString("it-IT") : "—"} {o.turno && `· Turno ${o.turno}`}</span>
                                        <span>{o.operatore_nome || "—"}</span>
                                    </div>
                                    <div className="text-xs font-mono text-slate-500 mb-2">
                                        {o.cartoni_prodotti} / {o.cartoni_pianificati} cartoni ({perc}%)
                                    </div>
                                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                        <div className={`h-full ${perc >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{width: `${Math.min(perc,100)}%`}} />
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Ultimi lotti creati */}
            <Card className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                    <Tag size={16} className="text-emerald-400" />
                    <h2 className="font-display font-bold text-lg">Ultimi lotti creati</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Lotto</th>
                                <th className="text-left px-5 py-3">Ordine</th>
                                <th className="text-left px-5 py-3">Articolo</th>
                                <th className="text-right px-5 py-3">Cartoni</th>
                                <th className="text-left px-5 py-3">Operatore</th>
                                <th className="text-left px-5 py-3">Stampa</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-lotti">
                            {lotti.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500 text-xs">Nessun lotto</td></tr>}
                            {lotti.map(l => <LottoRow key={l.id} l={l} user={user} onDone={load} onDelete={delLotto} />)}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

// ============================================================================
// Vista dettaglio ordine — 4 rettangoli read-only + pannello destro sticky
// ============================================================================
function OrderDetailView({ order, user, articles, fascette, tipiPed, cartoniAnag, colori, impianti, onBack, onRefresh }) {
    const [current, setCurrent] = useState(order);
    const [cartoniOrdine, setCartoniOrdine] = useState("");
    const [note, setNote] = useState("");
    const [extra, setExtra] = useState([]); // [{article_id, cartoni, note, colori:[{colore_codice,cartoni}]}]
    const [forzaLarghezza, setForzaLarghezza] = useState(false);
    // Suddivisione per colore quando l'articolo dell'ordine è COL
    const [coloriOrdine, setColoriOrdine] = useState([]); // [{colore_codice, cartoni}]
    // Prompt "Ordine al 100% — chiudere?" dopo aver dichiarato l'ultimo lotto
    const [askClose, setAskClose] = useState(false);

    // Riconosce come "COL" (articolo colorato generico) sia il codice canonico "COL"
    // sia varianti di nome "COLORATO/COLORATA" (case-insensitive, trim).
    const isColArticle = ((current.colore_codice || "").toUpperCase() === "COL")
        || (current.colore_nome || "").trim().toUpperCase().startsWith("COLORAT");
    // Elenco colori reali (esclude qualunque record 'COLORATO' generico) — usato nei menu a tendina
    const coloriReali = useMemo(() => (colori || []).filter(c => {
        const cb = (c.codice_breve || "").toUpperCase();
        const nm = (c.nome || "").trim().toUpperCase();
        return cb !== "COL" && !nm.startsWith("COLORAT");
    }), [colori]);

    const pesi = calcPesiOrdine(current);
    const fasc = fascette.find(f => f.id === current.fascetta_id_eff);
    const cart = cartoniAnag.find(c => c.id === current.cartone_id_eff);
    const ped = tipiPed.find(t => t.id === current.tipo_pedana_id_eff);
    const imp = impianti.find(i => i.id === current.impianto_id);

    const wOrdine = (current.larghezza_principale_eff || 0) + (current.larghezza_soffietto_eff || 0);
    const pisteOrdine = current.piste_eff || 1;
    const impiantoOrdine = impianti.find(i => i.id === current.impianto_id);
    const pisteMax = impiantoOrdine?.piste_max || 3;
    const bobina = wOrdine * pisteOrdine;

    // Filtro extra: articoli con larghezza ≈ bobina/N (N=1..pisteMax) ±3 cm, ognuno etichettato con N piste.
    // Se un articolo matcha più opzioni, tiene quella a piste più basse (più semplice).
    const TOLL = 3;
    const articoliCompatibili = useMemo(() => {
        if (forzaLarghezza || !bobina) return articles.map(a => ({...a, piste_suggerite: null}));
        const seen = new Map();
        for (let n = 1; n <= pisteMax; n++) {
            const target = bobina / n;
            for (const a of articles) {
                const w = (a.larghezza_principale||0) + (a.larghezza_soffietto||0);
                if (Math.abs(w - target) <= TOLL && !seen.has(a.id)) {
                    seen.set(a.id, {...a, piste_suggerite: n, w});
                }
            }
        }
        return Array.from(seen.values());
    }, [articles, bobina, pisteMax, forzaLarghezza]);

    const cartOrd = parseInt(cartoniOrdine) || 0;
    // Se ho righe colore per l'ordine principale: cartoni_ordine è auto-calcolato dalla somma
    const cartOrdColoriSum = coloriOrdine.reduce((s,r) => s + (parseInt(r.cartoni)||0), 0);
    const cartOrdEff = coloriOrdine.length > 0 ? cartOrdColoriSum : cartOrd;
    const cartExtra = extra.reduce((s,r) => s + (parseInt(r.cartoni)||0), 0);
    const canSalva = cartOrdEff > 0 || cartExtra > 0;
    const totalDopo = (current.cartoni_prodotti || 0) + cartOrdEff;
    const perc = current.cartoni_pianificati > 0 ? Math.min(100, Math.round(100 * totalDopo / current.cartoni_pianificati)) : 0;

    const canChiudere = current.stato === "aperto" && (current.cartoni_prodotti || 0) >= (current.cartoni_pianificati || 0);
    const canRiaprire = current.stato === "chiuso" && (user.role === "ufficio" || user.role === "admin" || current.chiuso_da_role === "operatore");

    const addExtra = () => setExtra([...extra, { article_id: "", cartoni: "", note: "", colori: [] }]);
    const removeExtra = (i) => setExtra(extra.filter((_,ix) => ix !== i));
    const updateExtra = (i, patch) => setExtra(extra.map((r,ix) => ix===i ? {...r, ...patch} : r));

    // Riga colore per l'ordine principale
    const addColoreOrdine = () => setColoriOrdine([...coloriOrdine, { colore_codice: "", cartoni: "" }]);
    const removeColoreOrdine = (i) => setColoriOrdine(coloriOrdine.filter((_,ix) => ix !== i));
    const updateColoreOrdine = (i, patch) => setColoriOrdine(coloriOrdine.map((r,ix) => ix===i ? {...r, ...patch} : r));

    // Righe colore per singola riga extra (quando l'articolo extra è COL)
    const extraArtColCodice = (idx) => {
        const a = articles.find(x => x.id === extra[idx]?.article_id);
        return (a?.colore_codice || "").toUpperCase();
    };
    const extraIsCol = (idx) => extraArtColCodice(idx) === "COL";
    const addColoreExtra = (i) => updateExtra(i, { colori: [...(extra[i].colori || []), { colore_codice: "", cartoni: "" }] });
    const removeColoreExtra = (i, j) => updateExtra(i, { colori: (extra[i].colori || []).filter((_,ix) => ix !== j) });
    const updateColoreExtra = (i, j, patch) => updateExtra(i, { colori: (extra[i].colori || []).map((r,ix) => ix===j ? {...r,...patch} : r) });

    const salva = async () => {
        if (!canSalva) { toast.error("Inserisci cartoni sull'ordine o almeno una riga extra"); return; }
        // valida righe extra
        for (const r of extra) {
            if (!r.article_id) { toast.error("Seleziona articolo per ogni riga extra"); return; }
            if (!parseInt(r.cartoni) || parseInt(r.cartoni) <= 0) { toast.error("Cartoni extra > 0"); return; }
        }
        // Valida righe colore (principale + per-extra)
        const validaColori = (rows, label) => {
            const codes = new Set();
            for (const r of rows) {
                if (!r.colore_codice) { toast.error(`${label}: seleziona il colore per ogni riga`); return false; }
                if (!parseInt(r.cartoni) || parseInt(r.cartoni) <= 0) { toast.error(`${label}: cartoni per colore > 0`); return false; }
                if (codes.has(r.colore_codice)) { toast.error(`${label}: colore '${r.colore_codice}' inserito più di una volta`); return false; }
                codes.add(r.colore_codice);
            }
            return true;
        };
        if (coloriOrdine.length > 0 && !validaColori(coloriOrdine, "Ordine")) return;
        for (let i=0; i<extra.length; i++) {
            const r = extra[i];
            if ((r.colori || []).length > 0 && !validaColori(r.colori, `Extra ${i+1}`)) return;
            // Se articolo extra è COL con righe colore: la somma deve combaciare
            if (extraIsCol(i) && (r.colori || []).length > 0) {
                const s = r.colori.reduce((a,x)=>a+(parseInt(x.cartoni)||0),0);
                if (s !== parseInt(r.cartoni)) { toast.error(`Extra ${i+1}: somma colori (${s}) diversa da cartoni (${r.cartoni})`); return; }
            }
        }
        // Se articolo principale è COL e ho cartoni ma NESSUN colore specificato → chiedi conferma
        if (isColArticle && coloriOrdine.length === 0 && cartOrd > 0) {
            const ok = window.confirm("Non hai specificato il colore reale.\nVuoi salvare comunque un lotto GENERICO su COL?");
            if (!ok) return;
        }
        try {
            const payload = {
                cartoni_ordine: coloriOrdine.length > 0 ? cartOrdColoriSum : cartOrd,
                note: note || null,
                extra: extra.map(r => ({
                    article_id: r.article_id, cartoni: parseInt(r.cartoni), note: r.note || null,
                    colori: (r.colori || []).map(c => ({ colore_codice: c.colore_codice, cartoni: parseInt(c.cartoni) })),
                })),
                forza_larghezza: forzaLarghezza,
                colori_ordine: coloriOrdine.map(c => ({ colore_codice: c.colore_codice, cartoni: parseInt(c.cartoni) })),
            };
            const { data } = await api.post(`/ordini/${current.id}/lotti-multipli`, payload);
            toast.success(`${data.lotti.length} lotto/i creati · pronti per stampa`);
            setCartoniOrdine(""); setNote(""); setExtra([]); setForzaLarghezza(false); setColoriOrdine([]);
            // Refresh completo (current + parent orders + lotti)
            const { data: refreshed } = await api.get("/ordini");
            const cur = refreshed.find(o => o.id === current.id);
            if (cur) {
                setCurrent(cur);
                // Se abbiamo raggiunto/superato la quantità pianificata, chiedi conferma chiusura
                if (cur.stato === "aperto" && cur.cartoni_pianificati > 0
                        && (cur.cartoni_prodotti || 0) >= cur.cartoni_pianificati) {
                    setAskClose(true);
                }
            }
            if (onRefresh) await onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doChiudi = async () => {
        try {
            await api.post(`/ordini/${current.id}/chiudi`);
            toast.success("Ordine chiuso — puoi riaprirlo dal pulsante qui sotto");
            const { data } = await api.get("/ordini");
            const cur = data.find(o => o.id === current.id); if (cur) setCurrent(cur);
            if (onRefresh) await onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const chiudi = async () => {
        if (!window.confirm(`Chiudere l'ordine ${current.numero}?`)) return;
        await doChiudi();
    };
    const riapri = async () => {
        if (!window.confirm(`Riaprire l'ordine ${current.numero}?`)) return;
        try {
            await api.post(`/ordini/${current.id}/riapri`);
            toast.success("Ordine riaperto");
            const { data } = await api.get("/ordini");
            const cur = data.find(o => o.id === current.id); if (cur) setCurrent(cur);
            if (onRefresh) await onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const RO = ({label, value, mono, cls=""}) => (
        <div>
            <Label className="text-xs text-slate-400">{label}</Label>
            <Input disabled value={value ?? "—"} className={`bg-slate-950/50 border-slate-800 mt-1 disabled:opacity-100 disabled:cursor-default ${mono?"font-mono":""} ${cls}`} />
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <Button onClick={onBack} variant="outline" size="sm" data-testid="button-back"
                        className="bg-transparent border-slate-700 gap-2"><ArrowLeft size={14} /> Indietro</Button>
                <div className="doc-code text-sm">{current.numero}</div>
                <div className="font-display text-2xl font-bold">{current.codice}</div>
                <div className="text-slate-400">{current.descrizione}</div>
                {current.multi_operatore && <Badge className="badge-pill bg-amber-500/15 text-amber-300 border-amber-500/40">MULTI-OP</Badge>}
                {current.stato === "chiuso" && (
                    <Badge className="badge-pill bg-slate-500/15 text-slate-300 border-slate-500/30">
                        CHIUSO {current.chiuso_da_role && `(${current.chiuso_da_role})`}
                    </Badge>
                )}
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
                {/* Colonna sinistra: 4 rettangoli read-only */}
                <div className="space-y-4">
                    {/* Rett.1 Identificativo */}
                    <div className="rounded-lg border border-violet-500/40 bg-violet-500/5 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300"><Hash size={12} /> Identificativo articolo</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <RO label="Cliente" value={current.cliente_eff} />
                            <div className="md:col-span-2"><RO label="Descrizione" value={current.descrizione} /></div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <RO label="Larg. principale (cm)" value={current.larghezza_principale_eff} mono />
                            <RO label="Soffietto (cm)" value={current.larghezza_soffietto_eff} mono />
                            <RO label="Lunghezza busta (cm)" value={current.lunghezza_eff} mono />
                            <RO label="Spessore (my)" value={current.spessore_my} mono />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <RO label="Colore" value={current.colore_nome} />
                            <RO label="Fascetta" value={fasc ? `${fasc.codice} · ${fasc.descrizione}` : "—"} />
                        </div>
                    </div>

                    {/* Rett.2 Pesi */}
                    <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-4">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 mb-3"><Scale size={12} /> Pesi</div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div><div className="text-slate-500 text-[10px] uppercase">Peso busta</div><div className="mt-0.5 font-mono text-emerald-300">{pesi.busta ? `${pesi.busta} g` : "—"}</div></div>
                            <div><div className="text-slate-500 text-[10px] uppercase">Peso rotolo</div><div className="mt-0.5 font-mono text-emerald-300">{pesi.rotolo ? `${pesi.rotolo} g` : "—"}</div></div>
                            <div><div className="text-slate-500 text-[10px] uppercase">Peso {imballoOf(current)==="vaschette"?"vaschetta":"cartone"}</div><div className="mt-0.5 font-mono text-emerald-300 font-bold">{pesi.cartone ? `${pesi.cartone} kg` : "—"}</div></div>
                        </div>
                    </div>

                    {/* Rett.3 Confezionamento */}
                    <div className="rounded-lg border border-violet-500/40 bg-violet-500/5 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300"><Package size={12} /> Confezionamento</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <RO label="Buste per rotolo" value={current.buste_per_rotolo_eff} mono />
                            <RO label={`Rotoli per ${imballoOf(current)==="vaschette"?"vaschetta":"cartone"}`} value={current.rotoli_per_cartone_eff} mono />
                            <RO label="Pedana" value={ped ? `${ped.codice} · ${ped.descrizione}` : "—"} />
                            {imballoOf(current) !== "vaschette" && (
                                <RO label="Cartone" value={cart ? `${cart.codice} · ${cart.descrizione}` : "—"} />
                            )}
                            <RO label={`${unitLabel(current, {title:true})} per pedana`} value={current.cartoni_per_pedana_eff} mono />
                            <RO label="Imballo" value={imballoOf(current) === "vaschette" ? "Vaschette" : "Cartoni"} />
                        </div>
                        {current.note_articolo && (
                            <div className="rounded-md border border-slate-700 bg-slate-950/60 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><FileText size={10} /> Note articolo</div>
                                <div className="text-xs text-slate-200 whitespace-pre-wrap" data-testid="op-note-articolo">{current.note_articolo}</div>
                            </div>
                        )}
                        {current.note && (
                            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1 flex items-center gap-1"><FileText size={10} /> Note ufficio</div>
                                <div className="text-xs text-slate-100 whitespace-pre-wrap" data-testid="op-note-ordine">{current.note}</div>
                            </div>
                        )}
                    </div>

                    {/* Rett.4 Lavorazione */}
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300"><CalendarClock size={12} /> Lavorazione</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <RO label="Macchina" value={imp ? `${imp.codice} · ${imp.descrizione}` : "—"} />
                            <RO label="Piste" value={current.piste_eff} mono />
                            <RO label="Turno" value={current.turno} mono />
                            <RO label="Data lavorazione" value={current.data_lavorazione ? new Date(current.data_lavorazione).toLocaleDateString("it-IT") : "—"} mono />
                        </div>
                        <RO label="Operatore" value={current.operatore_nome} />
                    </div>

                    {/* Quantità pianificata */}
                    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Quantità pianificata</div>
                        <div className="mt-1 font-mono text-2xl text-slate-100">{current.cartoni_pianificati} <span className="text-sm text-slate-500">{unitLabel(current)}</span></div>
                    </div>
                </div>

                {/* Colonna destra: pannello dichiarazione sticky — evidenziato */}
                <div className="lg:sticky lg:top-4 lg:self-start space-y-4 rounded-xl border-2 border-amber-500/60 bg-slate-950/80 p-5 shadow-[0_0_40px_-15px_rgba(245,158,11,0.4)]">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300">
                            <CheckCircle2 size={20} strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="text-base font-display font-extrabold tracking-wide text-amber-300 uppercase">Dichiara Produzione</div>
                            <div className="text-[10px] text-slate-500 font-mono tracking-widest uppercase">inserisci i cartoni prodotti</div>
                        </div>
                    </div>
                    {/* Progress più spesso + % grande */}
                    <div>
                        <div className="flex items-baseline justify-between mb-1.5">
                            <div className="text-xs font-mono text-slate-400">
                                {current.cartoni_prodotti} / {current.cartoni_pianificati} <span className="text-slate-600">{unitLabel(current)}</span>
                            </div>
                            <div className={`font-mono font-bold text-xl ${perc >= 100 ? "text-emerald-400" : "text-amber-300"}`}>
                                {perc}%
                            </div>
                        </div>
                        <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full transition-all ${perc >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{width: `${Math.min(perc,100)}%`}} />
                        </div>
                    </div>

                    {current.stato === "aperto" && (
                        <>
                            {isColArticle && (
                                <div className="rounded-md bg-emerald-500/5 border border-emerald-500/40 p-3 space-y-2">
                                    <div className="flex justify-between items-center">
                                        <div className="text-[10px] uppercase tracking-widest text-emerald-300">Suddivisione per colore</div>
                                        <button onClick={addColoreOrdine} data-testid="button-add-colore-ordine"
                                                className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><Plus size={12} /> Aggiungi colore</button>
                                    </div>
                                    <div className="text-[10px] text-slate-400">Articolo <span className="font-mono text-amber-300">COL</span> — dichiara i cartoni per ciascun colore reale. Ogni riga genererà un lotto separato con suffisso lettera (es. LT-2026-0007N).</div>
                                    {coloriOrdine.length === 0 && (
                                        <div className="text-[11px] text-slate-500 italic">Nessuna riga colore — verrà creato un lotto GENERICO su COL (previa conferma).</div>
                                    )}
                                    {coloriOrdine.map((r, i) => {
                                        const used = new Set(coloriOrdine.map((x,ix) => ix!==i ? x.colore_codice : null).filter(Boolean));
                                        return (
                                            <div key={i} className="flex gap-2 items-end" data-testid={`row-colore-ordine-${i}`}>
                                                <div className="flex-1 min-w-0">
                                                    <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1 block">Colore</Label>
                                                    <Select value={r.colore_codice} onValueChange={v => updateColoreOrdine(i, {colore_codice: v})}>
                                                        <SelectTrigger data-testid={`select-colore-ordine-${i}`} className="bg-slate-950/50 border-slate-800 h-9 text-xs w-full truncate">
                                                            <SelectValue placeholder="Seleziona colore…" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-[90vw]">
                                                            {coloriReali.filter(c => !used.has(c.codice_breve)).map(c => (
                                                                <SelectItem key={c.id} value={c.codice_breve}>
                                                                    <span className="text-xs">{c.nome}</span>
                                                                    <span className="font-mono text-amber-300 ml-2 text-[10px]">{c.codice_breve}</span>
                                                                </SelectItem>
                                                            ))}
                                                            {coloriReali.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Nessun colore in anagrafica — aggiungili in Anagrafiche → Colori</div>}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="w-24">
                                                    <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1 block">Cartoni</Label>
                                                    <Input data-testid={`input-cartoni-colore-ordine-${i}`} type="number" min="1" inputMode="numeric" placeholder="0"
                                                           value={r.cartoni} onChange={e => updateColoreOrdine(i, {cartoni: e.target.value})}
                                                           className="bg-slate-950/50 border-slate-800 h-9 font-mono text-center" />
                                                </div>
                                                <button onClick={() => removeColoreOrdine(i)} data-testid={`button-remove-colore-ordine-${i}`}
                                                        className="p-2 text-slate-500 hover:text-red-400 mb-0.5" title="Rimuovi colore">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                <Label className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">{unitLabel(current, {title:true})} prodott{imballoOf(current)==="vaschette"?"e":"i"} su questo ordine</Label>
                                <Input data-testid="input-cartoni-ordine" type="number" min="0" inputMode="numeric"
                                       value={coloriOrdine.length > 0 ? String(cartOrdColoriSum) : cartoniOrdine}
                                       onChange={e=>setCartoniOrdine(e.target.value)}
                                       disabled={coloriOrdine.length > 0}
                                       placeholder="0"
                                       title={coloriOrdine.length > 0 ? "Somma automatica delle righe colore" : ""}
                                       className={`bg-slate-950 border-2 border-amber-500/40 mt-2 font-mono h-16 text-center focus:border-amber-500 ${coloriOrdine.length>0?"disabled:opacity-90 disabled:cursor-default":""}`}
                                       style={{fontSize: "28pt", lineHeight: "1"}} />
                                {coloriOrdine.length > 0 && (
                                    <div className="text-[10px] text-emerald-300 mt-1 text-center font-mono">Auto-calcolato dalla somma delle righe colore</div>
                                )}
                            </div>

                            {/* Produzione extra */}
                            <div className="rounded-md bg-slate-950/40 border border-violet-500/30 p-3 space-y-3">
                                <div className="flex justify-between items-center">
                                    <div className="text-[10px] uppercase tracking-widest text-violet-300">Produzione extra</div>
                                    <button onClick={addExtra} data-testid="button-add-extra"
                                            className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><Plus size={12} /> Aggiungi</button>
                                </div>
                                <label className="flex items-center gap-2 text-[11px] text-amber-300 cursor-pointer">
                                    <input type="checkbox" checked={forzaLarghezza} onChange={e=>setForzaLarghezza(e.target.checked)} data-testid="check-forza-larghezza" />
                                    Forzatura larghezza differente
                                </label>
                                {forzaLarghezza && (
                                    <div className="text-[10px] text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 flex items-start gap-1">
                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                        Larghezza carta differente — verifica compatibilità macchina
                                    </div>
                                )}
                                {extra.map((r, i) => (
                                    <div key={i} className="rounded-md border border-slate-700/70 bg-slate-950/60 p-2.5 space-y-2" data-testid={`row-extra-${i}`}>
                                        <div className="flex gap-2 items-start">
                                            <div className="flex-1 min-w-0">
                                                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1 block">Articolo</Label>
                                                <Select value={r.article_id} onValueChange={v=>updateExtra(i,{article_id:v})}>
                                                    <SelectTrigger className="bg-slate-950/50 border-slate-800 h-9 text-xs w-full truncate">
                                                        <SelectValue placeholder="Seleziona articolo…" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-[90vw]">
                                                        {articoliCompatibili.map(a => <SelectItem key={a.id} value={a.id}>
                                                            <span className="font-mono text-amber-300 mr-2 text-[11px]">{a.codice}</span>
                                                            <span className="text-xs">{a.descrizione}</span>
                                                            {a.piste_suggerite && <span className="text-[10px] text-emerald-300 ml-2">· {a.piste_suggerite} pist{a.piste_suggerite===1?"a":"e"}</span>}
                                                        </SelectItem>)}
                                                        {articoliCompatibili.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Nessun articolo compatibile con bobina {bobina}</div>}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <button onClick={()=>removeExtra(i)} data-testid={`button-remove-extra-${i}`}
                                                    className="p-2 text-slate-500 hover:text-red-400 mt-5" title="Rimuovi riga">
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1 block">Cartoni extra</Label>
                                            <Input type="number" min="1" inputMode="numeric" placeholder="0" value={r.cartoni}
                                                   onChange={e=>updateExtra(i,{cartoni:e.target.value})}
                                                   data-testid={`input-cartoni-extra-${i}`}
                                                   className="bg-slate-950/50 border-slate-800 h-10 w-full font-mono text-center text-lg" />
                                        </div>
                                        {extraIsCol(i) && (
                                            <div className="rounded-md bg-emerald-500/5 border border-emerald-500/40 p-2 space-y-2 mt-2" data-testid={`extra-colori-${i}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="text-[10px] uppercase tracking-widest text-emerald-300">Suddivisione per colore (articolo COL)</div>
                                                    <button onClick={()=>addColoreExtra(i)} data-testid={`button-add-colore-extra-${i}`}
                                                            className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><Plus size={12} /> Colore</button>
                                                </div>
                                                {(r.colori || []).length === 0 && (
                                                    <div className="text-[10px] text-slate-500 italic">Nessuna riga colore — lotto GENERICO su COL.</div>
                                                )}
                                                {(r.colori || []).map((cr, j) => {
                                                    const usedE = new Set((r.colori || []).map((x, jx) => jx!==j ? x.colore_codice : null).filter(Boolean));
                                                    return (
                                                        <div key={j} className="flex gap-2 items-end" data-testid={`row-colore-extra-${i}-${j}`}>
                                                            <div className="flex-1 min-w-0">
                                                                <Select value={cr.colore_codice} onValueChange={v=>updateColoreExtra(i, j, {colore_codice: v})}>
                                                                    <SelectTrigger data-testid={`select-colore-extra-${i}-${j}`} className="bg-slate-950/50 border-slate-800 h-8 text-xs w-full truncate">
                                                                        <SelectValue placeholder="Colore…" />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-[90vw]">
                                                                        {coloriReali.filter(c => !usedE.has(c.codice_breve)).map(c => (
                                                                            <SelectItem key={c.id} value={c.codice_breve}>
                                                                                <span className="text-xs">{c.nome}</span>
                                                                                <span className="font-mono text-amber-300 ml-2 text-[10px]">{c.codice_breve}</span>
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <div className="w-20">
                                                                <Input data-testid={`input-cartoni-colore-extra-${i}-${j}`} type="number" min="1" inputMode="numeric" placeholder="0"
                                                                       value={cr.cartoni} onChange={e=>updateColoreExtra(i, j, {cartoni: e.target.value})}
                                                                       className="bg-slate-950/50 border-slate-800 h-8 font-mono text-center text-xs" />
                                                            </div>
                                                            <button onClick={()=>removeColoreExtra(i, j)} data-testid={`button-remove-colore-extra-${i}-${j}`}
                                                                    className="p-1.5 text-slate-500 hover:text-red-400" title="Rimuovi colore">
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div>
                                <Label className="text-xs text-slate-400">Note</Label>
                                <Textarea data-testid="input-lotto-note" value={note} onChange={e=>setNote(e.target.value)}
                                          className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                            </div>

                            <Button onClick={salva} disabled={!canSalva} data-testid="button-salva-lotti"
                                    className="w-full h-14 text-base bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold disabled:opacity-40 gap-2 shadow-lg shadow-emerald-500/20">
                                <Tag size={18} /> Salva lotto/i & genera etichette
                            </Button>
                        </>
                    )}

                    {/* Chiudi / Riapri */}
                    <div className="border-t border-slate-800 pt-3">
                        {current.stato === "aperto" ? (
                            <Button onClick={chiudi} disabled={!canChiudere && user.role === "operatore"}
                                    data-testid="button-chiudi-ordine"
                                    title={!canChiudere && user.role === "operatore" ? `Manca il minimo (${current.cartoni_prodotti}/${current.cartoni_pianificati} cartoni)` : ""}
                                    className="w-full bg-slate-800 hover:bg-slate-700 text-slate-100 disabled:opacity-40 gap-2">
                                <Lock size={14} /> Chiudi ordine
                            </Button>
                        ) : (
                            canRiaprire ? (
                                <Button onClick={riapri} data-testid="button-riapri-ordine"
                                        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                                    <Unlock size={14} /> Riapri ordine
                                </Button>
                            ) : (
                                <div className="text-xs text-slate-500 italic text-center">Chiuso dall'ufficio — non riapribile da operatore</div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Prompt automatico "Ordine al 100%: chiudere?" dopo l'ultima dichiarazione */}
            <Dialog open={askClose} onOpenChange={setAskClose}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-chiudi-100">
                    <DialogHeader>
                        <DialogTitle className="font-display text-emerald-300 flex items-center gap-2">
                            <CheckCircle2 size={20} /> Ordine al 100%
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-300 leading-relaxed">
                        Hai raggiunto la quantità pianificata:
                        <div className="mt-2 font-mono text-emerald-300 text-xl">
                            {current.cartoni_prodotti} / {current.cartoni_pianificati} <span className="text-slate-500 text-base">{unitLabel(current)}</span>
                        </div>
                        <div className="mt-3">Vuoi chiudere adesso l'ordine <b className="doc-code text-amber-300">{current.numero}</b>?</div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={()=>setAskClose(false)}
                                data-testid="btn-100-no"
                                className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                            No, lascia aperto
                        </Button>
                        <Button onClick={async ()=>{ setAskClose(false); await doChiudi(); }}
                                data-testid="btn-100-yes"
                                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold gap-2">
                            <Lock size={14} /> Sì, chiudi ordine
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
